// # Geographic Analysis Component
import React, { useState, useEffect, useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  BarChart
} from 'recharts';
import './geonalysis.css';
import { FaGlobeAmericas, FaChartPie, FaMapMarkerAlt, FaFlag, FaSpinner } from 'react-icons/fa';
import { nationalityData } from '../../../../../../utils/nationality_data';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';

// # Region colors for consistent visualization
const REGION_COLORS = {
  'Arab Countries': '#8884d8',
  'Asia': '#82ca9d',
  'Europe': '#ffc658',
  'Americas': '#ff8042',
  'Africa': '#a4de6c',
  'Oceania': '#d0ed57',
  'Other': '#cccccc'
};

// # Product categories mapping
const PRODUCT_CATEGORIES = {
  'step1_library': { type: 'Library', category: 'Step 1' },
  'step2_library': { type: 'Library', category: 'Step 2' },
  'step1_premium': { type: 'Premium', category: 'Step 1' },
  'step2_premium': { type: 'Premium', category: 'Step 2' },
  'step1_mentorship': { type: 'Mentorship', category: 'Step 1' },
  'step2_mentorship': { type: 'Mentorship', category: 'Step 2' },
  'step1_tutor_hosting': { type: 'Tutor Hosting', category: 'Step 1' },
  'step2_tutor_hosting': { type: 'Tutor Hosting', category: 'Step 2' },
  'OET': { type: 'Special', category: 'OET' },
  'US_VISA_Counselling': { type: 'Special', category: 'VISA' }
};

// # Helper function to determine region from nationality
const getRegionFromNationality = (nationality) => {
  if (!nationality) return 'Other';
  
  // Try to find in nationality data
  const nationalityEntry = Object.entries(nationalityData).find(([_, data]) => 
    data.name.toLowerCase() === nationality.toLowerCase() ||
    data.searchKeys.some(key => nationality.toLowerCase().includes(key.toLowerCase()))
  );
  
  if (nationalityEntry) {
    const [_, data] = nationalityEntry;
    
    // Determine region based on search keys
    if (data.searchKeys.includes('arab')) return 'Arab Countries';
    if (data.searchKeys.includes('asia') && !data.searchKeys.includes('arab')) return 'Asia';
    if (data.searchKeys.includes('europe') && !data.searchKeys.includes('asia')) return 'Europe';
    if (data.searchKeys.includes('americas')) return 'Americas';
    if (data.searchKeys.includes('africa') && !data.searchKeys.includes('arab')) return 'Africa';
    if (data.searchKeys.includes('oceania')) return 'Oceania';
  }
  
  return 'Other';
};

// # Helper function to get country flag emoji
const getCountryFlag = (nationality) => {
  if (!nationality) return '🌍';
  
  // Try to find in nationality data
  const nationalityEntry = Object.entries(nationalityData).find(([_, data]) => 
    data.name.toLowerCase() === nationality.toLowerCase() ||
    data.searchKeys.some(key => nationality.toLowerCase().includes(key.toLowerCase()))
  );
  
  if (nationalityEntry) {
    return nationalityEntry[1].flag;
  }
  
  return '🌍';
};

const GeoAnalysis = () => {
  // # Add state for data and loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({ payments: [] });
  const [timeFilter, setTimeFilter] = useState('all'); // Add time filter state

  // # Fetch payments data from Firestore
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // # Get all documents from financial collection
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        let allPayments = [];
        
        // # Process each year_month document
        for (const doc of financialSnap.docs) {
          const yearMonthData = doc.data();
          
          // # Extract payments from the payments map
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, paymentData]) => {
              // # Only process payment transactions
              if (paymentId.startsWith('payment_') && paymentData.transaction_type === 'payment') {
                allPayments.push({
                  payment_id: paymentId,
                  doc_id: doc.id,
                  user: paymentData.user || {},
                  pay: paymentData.pay || {},
                  budget_dvd: paymentData.budget_dvd || {},
                  metadata: paymentData.metadata || {},
                  mentor: paymentData.mentor || {},
                  tutor: paymentData.tutor || {},
                  shares: paymentData.shares || {}
                });
              }
            });
          }
        }

        // # Sort payments by date
        allPayments.sort((a, b) => {
          const dateA = new Date(a.metadata.created_at || 0);
          const dateB = new Date(b.metadata.created_at || 0);
          return dateB - dateA;
        });

        setAnalyticsData({ payments: allPayments });
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('Failed to load geographic data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  // # Process geographic data with better error handling and nationality mapping
  const processedData = useMemo(() => {
    // # Validate input data
    if (!analyticsData || !analyticsData.payments || !Array.isArray(analyticsData.payments)) {
      return {
        countryData: {},
        regionData: [],
        productData: [],
        topCountries: [],
        globalDistribution: [],
        timeSeriesData: [],
        productPerformance: []
      };
    }
    
    // # Initialize data structures
    const countryData = {};
    const regionTotals = {
      'Arab Countries': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Arab Countries'] },
      'Asia': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Asia'] },
      'Europe': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Europe'] },
      'Americas': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Americas'] },
      'Africa': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Africa'] },
      'Oceania': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Oceania'] },
      'Other': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, color: REGION_COLORS['Other'] }
    };
    
    // # Add timeSeriesData to store time-based revenue data
    const timeSeriesData = {};
    
    // # Add productPerformanceData to store region-based product metrics
    const productPerformanceData = {
      byRegion: {
        'Arab Countries': { revenue: 0, sales: 0, byMonth: {} },
        'Asia': { revenue: 0, sales: 0, byMonth: {} },
        'Europe': { revenue: 0, sales: 0, byMonth: {} },
        'Americas': { revenue: 0, sales: 0, byMonth: {} },
        'Africa': { revenue: 0, sales: 0, byMonth: {} },
        'Oceania': { revenue: 0, sales: 0, byMonth: {} },
        'Other': { revenue: 0, sales: 0, byMonth: {} }
      }
    };
    
    // # Process payments data
    analyticsData.payments.forEach(payment => {
      if (payment && payment.user && payment.pay && payment.metadata?.created_at) {
        // # Get proper region for this payment's nationality
        const properRegion = getRegionFromNationality(payment.user.nationality);
        const countryName = payment.user.nationality || 'Unknown';
        
        // # Get payment amounts and date
        const totalAmount = Number(payment.pay.egp_total) || 0;
        const netAmount = Number(payment.pay.egp_net) || 0;
        const date = new Date(payment.metadata.created_at);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        // # Initialize time series data for this month if not exists
        if (!timeSeriesData[monthYear]) {
          timeSeriesData[monthYear] = {
            date: monthYear,
            'Arab Countries': 0,
            'Asia': 0,
            'Europe': 0,
            'Americas': 0,
            'Africa': 0,
            'Oceania': 0,
            'Other': 0,
            total: 0
          };
        }
        
        // # Update time series data
        timeSeriesData[monthYear][properRegion] += totalAmount;
        timeSeriesData[monthYear].total += totalAmount;
        
        // # Update country data
        if (!countryData[countryName]) {
          countryData[countryName] = {
            totalRevenue: 0,
            netRevenue: 0,
            transactions: 0,
            flag: getCountryFlag(countryName),
            region: properRegion,
            regionColor: REGION_COLORS[properRegion] || REGION_COLORS['Other'],
            products: {}
          };
        }
        
        countryData[countryName].totalRevenue += totalAmount;
        countryData[countryName].netRevenue += netAmount;
        countryData[countryName].transactions += 1;
        
        // # Update product data for country
        const productType = payment.pay.product || 'unknown';
        if (!countryData[countryName].products[productType]) {
          countryData[countryName].products[productType] = {
            count: 0,
            totalRevenue: 0,
            netRevenue: 0
          };
        }
        
        countryData[countryName].products[productType].count += 1;
        countryData[countryName].products[productType].totalRevenue += totalAmount;
        countryData[countryName].products[productType].netRevenue += netAmount;
        
        // # Update region totals
        if (regionTotals[properRegion]) {
          regionTotals[properRegion].totalRevenue += totalAmount;
          regionTotals[properRegion].netRevenue += netAmount;
          regionTotals[properRegion].transactionCount += 1;
        }

        // # Process product performance data
        productPerformanceData.byRegion[properRegion].revenue += totalAmount;
        productPerformanceData.byRegion[properRegion].sales += 1;

        // # Initialize month data if not exists
        if (!productPerformanceData.byRegion[properRegion].byMonth[monthYear]) {
          productPerformanceData.byRegion[properRegion].byMonth[monthYear] = {
            revenue: 0,
            sales: 0
          };
        }

        // # Update month data
        productPerformanceData.byRegion[properRegion].byMonth[monthYear].revenue += totalAmount;
        productPerformanceData.byRegion[properRegion].byMonth[monthYear].sales += 1;
      }
    });
    
    // # Create region data array
    const regionData = Object.entries(regionTotals)
      .filter(([_, data]) => data.totalRevenue > 0) // Only include regions with revenue
      .map(([region, data]) => ({
        region,
        totalRevenue: data.totalRevenue,
        netRevenue: data.netRevenue,
        transactionCount: data.transactionCount,
        averageTransaction: data.transactionCount > 0 ? 
          data.totalRevenue / data.transactionCount : 0,
        color: data.color
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
    
    // # Create global distribution data for pie chart
    const globalDistribution = regionData.map(region => ({
      name: region.region,
      value: region.totalRevenue,
      netValue: region.netRevenue,
      color: region.color
    }));
    
    // # Get top countries by revenue
    const topCountries = Object.entries(countryData)
      .map(([country, data]) => ({
        country,
        ...data
      }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 15); // Show top 15 countries
    
    // # Process product data by region
    const productData = Object.entries(regionTotals)
      .filter(([_, data]) => data.totalRevenue > 0)
      .map(([region, data]) => {
        const regionProducts = {};
        
        // Aggregate product data for this region
        Object.entries(countryData)
          .filter(([_, countryData]) => countryData.region === region)
          .forEach(([_, countryData]) => {
            Object.entries(countryData.products).forEach(([product, productData]) => {
              if (!regionProducts[product]) {
                regionProducts[product] = {
                  count: 0,
                  totalRevenue: 0,
                  netRevenue: 0
                };
              }
              regionProducts[product].count += productData.count;
              regionProducts[product].totalRevenue += productData.totalRevenue;
              regionProducts[product].netRevenue += productData.netRevenue;
            });
          });
        
        return {
          region,
          color: REGION_COLORS[region] || REGION_COLORS['Other'],
          products: Object.entries(regionProducts)
            .map(([product, data]) => ({
              product,
              ...data,
              productType: PRODUCT_CATEGORIES[product]?.type || 'Other',
              category: PRODUCT_CATEGORIES[product]?.category || 'Other'
            }))
            .sort((a, b) => b.totalRevenue - a.totalRevenue)
        };
      });
    
    // # Convert timeSeriesData to array and sort by date
    const timeSeriesArray = Object.values(timeSeriesData)
      .sort((a, b) => a.date.localeCompare(b.date));

    // # Convert product performance data to array format for charts
    const productPerformanceArray = Object.entries(productPerformanceData.byRegion)
      .map(([region, data]) => ({
        region,
        revenue: data.revenue,
        sales: data.sales,
        color: REGION_COLORS[region],
        monthlyData: Object.entries(data.byMonth).map(([month, monthData]) => ({
          month,
          revenue: monthData.revenue,
          sales: monthData.sales
        })).sort((a, b) => a.month.localeCompare(b.month))
      }))
      .filter(region => region.revenue > 0 || region.sales > 0);

    return {
      countryData,
      regionData,
      productData,
      topCountries,
      globalDistribution,
      timeSeriesData: timeSeriesArray,
      productPerformance: productPerformanceArray
    };
  }, [analyticsData]);
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${Number(value).toLocaleString()}`;
  };
  
  // # Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = processedData.globalDistribution.reduce((sum, item) => sum + item.value, 0);
      return (
        <div className="gnlsys_custom_tooltip">
          <p className="gnlsys_tooltip_label">{data.name}</p>
          <p className="gnlsys_tooltip_value">
            Total: {formatCurrency(data.value)}<br/>
            Net: {formatCurrency(data.netValue)}
          </p>
          <p className="gnlsys_tooltip_percent">
            {((data.value / total) * 100).toFixed(1)}% of total revenue
          </p>
        </div>
      );
    }
    return null;
  };

  // # Helper function to filter data based on time range
  const filterDataByTimeRange = (data, timeFilter) => {
    if (timeFilter === 'lastYear') {
      const lastYear = new Date();
      lastYear.setFullYear(lastYear.getFullYear() - 1);
      
      return data.map(region => ({
        ...region,
        revenue: region.monthlyData
          .filter(month => new Date(month.month) >= lastYear)
          .reduce((sum, month) => sum + month.revenue, 0),
        sales: region.monthlyData
          .filter(month => new Date(month.month) >= lastYear)
          .reduce((sum, month) => sum + month.sales, 0)
      }));
    }
    return data;
  };

  // # Update the return statement to handle loading and error states
  if (loading) {
    return (
      <div className="gnlsys_container">
        <div className="gnlsys_loading">
          <FaSpinner className="gnlsys_spinner" />
          <p>Loading geographic analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gnlsys_container">
        <div className="gnlsys_error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gnlsys_container">
      {!analyticsData.payments || analyticsData.payments.length === 0 ? (
        <div className="gnlsys_no_data">
          <h3>Geographic data is not available</h3>
          <p>Please ensure the geographic data is properly formatted and available.</p>
        </div>
      ) : (
        <>
          {/* Global Revenue Distribution - Now a Pie Chart */}
          <div className="gnlsys_chart_container">
            <h3>Global Revenue Distribution</h3>
            <div className="gnlsys_global_distribution">
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={processedData.globalDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={150}
                    fill="#8884d8"
                    labelLine={true}
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  >
                    {processedData.globalDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Updated Regional Overview - Time Series Line Chart */}
          <div className="gnlsys_chart_container">
            <h3>Revenue Distribution by Region Over Time</h3>
            <div className="gnlsys_region_charts">
              <div className="gnlsys_line_chart">
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={processedData.timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Revenue (EGP)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle' }
                      }}
                    />
                    <Tooltip 
                      formatter={(value, name) => [
                        formatCurrency(value),
                        name === 'total' ? 'Total Revenue' : name
                      ]}
                    />
                    <Legend />
                    {Object.keys(REGION_COLORS).map(region => (
                      <Line
                        key={region}
                        type="monotone"
                        dataKey={region}
                        name={region}
                        stroke={REGION_COLORS[region]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Total Revenue"
                      stroke="#000"
                      strokeWidth={3}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Countries Table */}
          <div className="gnlsys_table_container">
            <h3>Top Performing Countries</h3>
            <div className="gnlsys_table">
              <table>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Region</th>
                    <th>Revenue (EGP)</th>
                    <th>Transactions</th>
                    <th>Avg. Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.topCountries.map((country, index) => (
                    <tr key={index}>
                      <td>
                        <span className="gnlsys_flag">{country.flag}</span>
                        {country.country}
                      </td>
                      <td>
                        <span className="gnlsys_region_badge" style={{ backgroundColor: country.regionColor }}>
                          {country.region}
                        </span>
                      </td>
                      <td>{formatCurrency(country.totalRevenue)}</td>
                      <td>{country.transactions}</td>
                      <td>
                        {formatCurrency(country.transactions ? 
                          country.totalRevenue / country.transactions : 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Product Performance by Region */}
          <div className="gnlsys_chart_container">
            <h3>Product Performance by Region</h3>
            <div className="gnlsys_filter_controls">
              <select 
                value={timeFilter} 
                onChange={(e) => setTimeFilter(e.target.value)}
                className="gnlsys_time_filter"
              >
                <option value="all">All Time</option>
                <option value="lastYear">Last Year</option>
              </select>
            </div>
            <div className="gnlsys_product_charts">
              {/* Revenue Chart */}
              <div className="gnlsys_region_product_chart">
                <h4>Revenue by Region</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={filterDataByTimeRange(processedData.productPerformance, timeFilter)}
                    margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="region" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Revenue (EGP)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle' }
                      }}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend />
                    <Bar 
                      dataKey="revenue" 
                      name="Revenue" 
                      fill="#8884d8"
                    >
                      {processedData.productPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Sales Count Chart */}
              <div className="gnlsys_region_product_chart">
                <h4>Number of Sales by Region</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={filterDataByTimeRange(processedData.productPerformance, timeFilter)}
                    margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="region" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Number of Sales', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle' }
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="sales" 
                      name="Sales Count" 
                      fill="#82ca9d"
                    >
                      {processedData.productPerformance.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GeoAnalysis;
